import React, { useState, useRef, useEffect } from "react";
import MaterialTable from "material-table";
import axios from "axios";
import {
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  Grid,
  IconButton,
  MenuItem,
  Paper,
  TextField,
  Typography,
} from "@material-ui/core";
import { Autocomplete } from "@material-ui/lab";
import HeaderAndLayout from "../layouts/HeaderAndLayout";
import BulkUploadDialog from "./BulkUploadDialog";
import ConfirmDialog from "../common/ConfirmDialog";
import ExportFormatDialog from "../common/ExportFormatDialog";
import { toggleTableRowSelection } from "../common/toggleTableRowSelection";
import {
  bindPageOnlyHeaderSelection,
  clearTableSelection,
  selectAllRows,
} from "../common/materialTableSelection";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import CloudUploadIcon from "@material-ui/icons/CloudUpload";
import CloudDownloadIcon from "@material-ui/icons/CloudDownload";
import DeleteIcon from "@material-ui/icons/Delete";
import BeatLoader from "react-spinners/BeatLoader";

const STAGE_OPTIONS = ["Idea", "MVP", "Early Customers", "Fundraising", "Scaling"];
const MAIN_GOAL_OPTIONS = ["Validation", "Growth", "Fundraising", "Operations"];
const TASK_PRIORITY_OPTIONS = ["low", "medium", "high"];
const TASK_EFFORT_OPTIONS = ["low", "medium", "high"];
const STARTUP_TYPE_OPTIONS = [
  "SaaS",
  "E-commerce",
  "Marketplace",
  "Mobile App",
  "Hardware",
  "FinTech",
  "HealthTech",
  "EdTech",
  "Food & Beverage",
  "Real Estate",
  "Travel & Tourism",
  "Entertainment",
  "Other",
];

const normalizeEffortLevel = (effortEstimate) => {
  const normalized = `${effortEstimate || ""}`.trim().toLowerCase();
  if (TASK_EFFORT_OPTIONS.includes(normalized)) return normalized;
  return "medium";
};

const normalizeStartupTypes = (startupTypes) => {
  if (!Array.isArray(startupTypes)) return [];
  return startupTypes
    .map((type) => `${type || ""}`.trim())
    .filter(Boolean);
};

const createEmptyTask = () => ({
  title: "",
  description: "",
  effortEstimate: "medium",
  xpValue: 10,
  priority: "medium",
  suggestedResourceCodes: [],
});

const createEmptyMilestone = () => ({
  name: "",
  description: "",
  targetOffsetDays: 0,
  keywordsInput: "",
  tasks: [createEmptyTask()],
});

const defaultTemplate = {
  name: "",
  description: "",
  status: "draft",
  criteriaStage: "",
  criteriaMainGoals: [],
  criteriaStartupTypes: [],
  criteriaStartupTypeInput: "",
  criteriaStartupTypeCustomInput: "",
  criteriaHasPrototype: "any",
  criteriaHasPayingUsers: "any",
  milestones: [],
};

const splitCommaValues = (value) =>
  `${value || ""}`
    .split(",")
    .map((item) => item.trim())
    .filter(Boolean);

const toFormValues = (template) => {
  const criteria = template?.matchCriteria || {};
  return {
    name: template?.name || "",
    description: template?.description || "",
    status: template?.status || "draft",
    criteriaStage: Array.isArray(criteria.stage) ? criteria.stage[0] || "" : "",
    criteriaMainGoals: Array.isArray(criteria.mainGoal) ? criteria.mainGoal : [],
    criteriaStartupTypes: normalizeStartupTypes(criteria.startupType),
    criteriaStartupTypeInput: "",
    criteriaStartupTypeCustomInput: "",
    criteriaHasPrototype:
      typeof criteria.hasPrototype === "boolean"
        ? criteria.hasPrototype
          ? "yes"
          : "no"
        : "any",
    criteriaHasPayingUsers:
      typeof criteria.hasPayingUsers === "boolean"
        ? criteria.hasPayingUsers
          ? "yes"
          : "no"
        : "any",
    milestones: Array.isArray(template?.milestones)
      ? template.milestones.map((milestone) => ({
          name: milestone?.name || "",
          description: milestone?.description || "",
          targetOffsetDays:
            milestone?.targetOffsetDays !== undefined && milestone?.targetOffsetDays !== null
              ? milestone.targetOffsetDays
              : 0,
          keywordsInput: Array.isArray(milestone?.keywords)
            ? milestone.keywords.join(", ")
            : "",
          tasks: Array.isArray(milestone?.tasks)
            ? milestone.tasks.map((task) => ({
                title: task?.title || "",
                description: task?.description || "",
                effortEstimate: normalizeEffortLevel(task?.effortEstimate),
                xpValue:
                  task?.xpValue !== undefined && task?.xpValue !== null ? task.xpValue : 10,
                priority: task?.priority || "medium",
                suggestedResourceCodes: Array.isArray(task?.suggestedResourceCodes)
                  ? task.suggestedResourceCodes
                      .map((code) => `${code || ""}`.trim())
                      .filter(Boolean)
                  : [],
              }))
            : [],
        }))
      : [],
  };
};

function RoadmapTemplates() {
  const tableRef = useRef(null);
  const [tableData, setTableData] = useState([]);
  const [totalCount, setTotalCount] = useState(0);
  const [selectedRows, setSelectedRows] = useState([]);
  const [allSelectedMode, setAllSelectedMode] = useState(false);
  const [allSelectedIds, setAllSelectedIds] = useState([]);
  const [selectingAll, setSelectingAll] = useState(false);
  const allSelectedModeRef = useRef(false);
  const allSelectedIdsRef = useRef([]);
  const [loading, setLoading] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [exportDialogOpen, setExportDialogOpen] = useState(false);
  const [open, setOpen] = useState(false);
  const [bulkUploadOpen, setBulkUploadOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [formValues, setFormValues] = useState(defaultTemplate);
  const [confirmDialog, setConfirmDialog] = useState({
    open: false,
    title: "",
    message: "",
    ids: [],
  });
  const [resourceSearchOptions, setResourceSearchOptions] = useState([]);
  const [resourceSearchLoading, setResourceSearchLoading] = useState(false);
  const resourceSearchTimer = useRef(null);

  const axiosConfig = {
    headers: {
      Authorization: `Bearer ${localStorage.getItem("token")}`,
      "Content-Type": "application/json",
    },
  };

  const searchResourcesForTask = (query) => {
    if (resourceSearchTimer.current) {
      clearTimeout(resourceSearchTimer.current);
    }
    const term = `${query || ""}`.trim();
    if (term.length < 2) {
      setResourceSearchOptions([]);
      setResourceSearchLoading(false);
      return;
    }
    setResourceSearchLoading(true);
    resourceSearchTimer.current = setTimeout(async () => {
      try {
        const { data } = await axios.get(
          `/resource/search?name=${encodeURIComponent(term)}`,
          axiosConfig
        );
        const list = Array.isArray(data) ? data : data?.data || [];
        setResourceSearchOptions(
          list
            .filter((item) => item?.code)
            .map((item) => ({
              code: item.code,
              name: item.name || item.code,
            }))
        );
      } catch (error) {
        setResourceSearchOptions([]);
      } finally {
        setResourceSearchLoading(false);
      }
    }, 300);
  };

  const setAllSelectionState = (enabled, ids = []) => {
    allSelectedModeRef.current = enabled;
    allSelectedIdsRef.current = ids;
    setAllSelectedMode(enabled);
    setAllSelectedIds(ids);
  };

  const refreshTable = () => {
    setSelectedRows([]);
    setAllSelectionState(false, []);
    if (tableRef.current?.onQueryChange) {
      tableRef.current.onQueryChange();
    }
  };

  const applyChecksForIds = (rows, idList) => {
    const idSet = new Set((idList || []).map(String));
    return (rows || []).map((row) => {
      const checked = idSet.has(String(row._id));
      return {
        ...row,
        tableData: {
          ...(row.tableData || {}),
          checked,
        },
      };
    });
  };

  const fetchTablePage = async (query) => {
    const page = (query?.page || 0) + 1;
    const limit = query?.pageSize || 10;
    const search = query?.search || "";
    const { data } = await axios.get("/roadmap-template/admin", {
      ...axiosConfig,
      params: { page, limit, search },
    });
    let rows = data?.data || [];
    const total = data?.total ?? rows.length;
    setTableData(rows);
    setTotalCount(total);

    if (allSelectedModeRef.current && allSelectedIdsRef.current.length) {
      rows = applyChecksForIds(rows, allSelectedIdsRef.current);
      // Keep full id list as selection source for bulk actions.
      setSelectedRows(
        allSelectedIdsRef.current.map((id) => ({ _id: id }))
      );
    } else {
      setSelectedRows([]);
    }

    return {
      data: rows,
      page: (data?.page || page) - 1,
      totalCount: total,
    };
  };

  const fetchAllTemplateRows = async () => {
    const search = tableRef.current?.state?.query?.search || "";
    const pageSize = 100;
    let page = 1;
    let total = Infinity;
    const all = [];
    while (all.length < total) {
      const { data } = await axios.get("/roadmap-template/admin", {
        ...axiosConfig,
        params: { page, limit: pageSize, search },
      });
      const rows = data?.data || [];
      total = data?.total ?? all.length + rows.length;
      all.push(...rows);
      if (!rows.length) break;
      page += 1;
      if (page > 500) break;
    }
    return all;
  };

  const downloadBlob = (blob, filename) => {
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    window.URL.revokeObjectURL(url);
  };

  const handleExport = async (format = "xlsx") => {
    try {
      setExporting(true);
      const response = await axios.get(`/roadmap-template/admin/export?format=${format}`, {
        ...axiosConfig,
        responseType: "blob",
      });
      const filename =
        format === "csv"
          ? "roadmap-templates-export.csv"
          : "roadmap-templates-export.xlsx";
      downloadBlob(response.data, filename);
      toast.success(
        `Exported ${totalCount || "all"} template(s). Sheet is ready for bulk import.`
      );
      setExportDialogOpen(false);
    } catch (error) {
      toast.error(error?.response?.data?.message || "Failed to export templates");
    } finally {
      setExporting(false);
    }
  };

  const requestDeleteByIds = (ids, label) => {
    if (!ids.length) {
      toast.info("No templates selected");
      return;
    }
    setConfirmDialog({
      open: true,
      title: "Delete templates",
      message: `Delete ${ids.length} ${label}? This cannot be undone.`,
      ids,
    });
  };

  const handleConfirmDelete = async () => {
    const ids = confirmDialog.ids || [];
    if (!ids.length) {
      setConfirmDialog({ open: false, title: "", message: "", ids: [] });
      return;
    }
    try {
      setDeleting(true);
      const { data } = await axios.post(
        "/roadmap-template/admin/bulk-delete",
        { ids },
        axiosConfig
      );
      toast.success(`Deleted ${data?.summary?.deleted || 0} template(s).`);
      setConfirmDialog({ open: false, title: "", message: "", ids: [] });
      refreshTable();
    } catch (error) {
      toast.error(error?.response?.data?.message || "Failed to delete templates");
    } finally {
      setDeleting(false);
    }
  };

  useEffect(() => {
    if (!tableData.length) return;
    bindPageOnlyHeaderSelection(tableRef, setSelectedRows, () => {
      setAllSelectionState(false, []);
    });
  }, [tableData.length, loading]);

  const handleSelectAll = async () => {
    if (allSelectedModeRef.current) {
      clearTableSelection(tableRef, setSelectedRows);
      setAllSelectionState(false, []);
      return;
    }

    try {
      setSelectingAll(true);
      const allRows = await fetchAllTemplateRows();
      const ids = allRows.map((row) => row._id).filter(Boolean);
      setAllSelectionState(true, ids);
      // Visually check the current page; selection source is the full id list.
      selectAllRows(tableRef, () => {}, true, tableData);
      setSelectedRows(ids.map((id) => ({ _id: id })));
      toast.info(`Selected all ${ids.length} template(s)`);
    } catch (error) {
      toast.error(
        error?.response?.data?.message || "Failed to select all templates"
      );
    } finally {
      setSelectingAll(false);
    }
  };

  const handleDeleteSelected = () => {
    const ids = allSelectedModeRef.current
      ? allSelectedIdsRef.current
      : selectedRows.map((row) => row._id).filter(Boolean);
    requestDeleteByIds(ids, "selected template(s)");
  };

  const resetForm = () => {
    setEditing(null);
    setFormValues(defaultTemplate);
    setOpen(false);
  };

  const openCreate = () => {
    setEditing(null);
    setFormValues({
      ...defaultTemplate,
      milestones: [createEmptyMilestone()],
    });
    setOpen(true);
  };

  const openEdit = async (row) => {
    if (!row?._id) {
      toast.error("Cannot edit: template id is missing.");
      return;
    }
    try {
      setLoading(true);
      const { data } = await axios.get(
        `/roadmap-template/admin/${row._id}`,
        axiosConfig
      );
      const fullTemplate = data?.data || row;
      setEditing(fullTemplate);
      const hydrated = toFormValues(fullTemplate);
      setFormValues({
        ...hydrated,
        milestones:
          hydrated.milestones.length > 0
            ? hydrated.milestones
            : [createEmptyMilestone()],
      });
      setOpen(true);
    } catch (error) {
      toast.error(error?.response?.data?.message || "Failed to load template");
    } finally {
      setLoading(false);
    }
  };

  const onChange = (e) => {
    setFormValues((prev) => ({
      ...prev,
      [e.target.name]: e.target.value,
    }));
  };

  const onStartupTypeSelectChange = (e) => {
    const selected = e.target.value;

    if (!selected) {
      setFormValues((prev) => ({
        ...prev,
        criteriaStartupTypeInput: "",
      }));
      return;
    }

    // For non-"Other", add immediately on selection.
    if (selected !== "Other") {
      const alreadyExists = (formValues.criteriaStartupTypes || []).some(
        (item) => item.toLowerCase() === selected.toLowerCase()
      );
      if (alreadyExists) {
        toast.info("Startup type already added");
        setFormValues((prev) => ({ ...prev, criteriaStartupTypeInput: "" }));
        return;
      }

      setFormValues((prev) => ({
        ...prev,
        criteriaStartupTypes: [...(prev.criteriaStartupTypes || []), selected],
        criteriaStartupTypeInput: "",
        criteriaStartupTypeCustomInput: "",
      }));
      return;
    }

    // For "Other", just show custom input; add will happen via Enter in the custom field.
    setFormValues((prev) => ({
      ...prev,
      criteriaStartupTypeInput: "Other",
    }));
  };

  const addOtherStartupType = () => {
    const selectedType = "Other";
    const customType = `${formValues.criteriaStartupTypeCustomInput || ""}`.trim();
    const startupTypeToAdd = customType ? customType : selectedType; // if no custom, treat as wildcard "Other"

    const alreadyExists = (formValues.criteriaStartupTypes || []).some(
      (item) => item.toLowerCase() === startupTypeToAdd.toLowerCase()
    );
    if (alreadyExists) {
      toast.info("Startup type already added");
      return;
    }

    setFormValues((prev) => ({
      ...prev,
      criteriaStartupTypes: [...(prev.criteriaStartupTypes || []), startupTypeToAdd],
      criteriaStartupTypeInput: "",
      criteriaStartupTypeCustomInput: "",
    }));
  };

  const removeStartupType = (startupTypeToRemove) => {
    setFormValues((prev) => ({
      ...prev,
      criteriaStartupTypes: (prev.criteriaStartupTypes || []).filter(
        (item) => item !== startupTypeToRemove
      ),
    }));
  };

  const updateMilestoneField = (milestoneIndex, field, value) => {
    setFormValues((prev) => {
      const milestones = [...prev.milestones];
      milestones[milestoneIndex] = {
        ...milestones[milestoneIndex],
        [field]: value,
      };
      return { ...prev, milestones };
    });
  };

  const updateTaskField = (milestoneIndex, taskIndex, field, value) => {
    setFormValues((prev) => {
      const milestones = [...prev.milestones];
      const tasks = [...(milestones[milestoneIndex]?.tasks || [])];
      tasks[taskIndex] = {
        ...tasks[taskIndex],
        [field]: value,
      };
      milestones[milestoneIndex] = {
        ...milestones[milestoneIndex],
        tasks,
      };
      return { ...prev, milestones };
    });
  };

  const addMilestone = () => {
    setFormValues((prev) => ({
      ...prev,
      milestones: [...prev.milestones, createEmptyMilestone()],
    }));
  };

  const removeMilestone = (milestoneIndex) => {
    setFormValues((prev) => {
      const milestones = prev.milestones.filter((_, idx) => idx !== milestoneIndex);
      return {
        ...prev,
        milestones: milestones.length > 0 ? milestones : [createEmptyMilestone()],
      };
    });
  };

  const addTask = (milestoneIndex) => {
    setFormValues((prev) => {
      const milestones = [...prev.milestones];
      const tasks = [...(milestones[milestoneIndex]?.tasks || []), createEmptyTask()];
      milestones[milestoneIndex] = {
        ...milestones[milestoneIndex],
        tasks,
      };
      return { ...prev, milestones };
    });
  };

  const removeTask = (milestoneIndex, taskIndex) => {
    setFormValues((prev) => {
      const milestones = [...prev.milestones];
      const tasks = (milestones[milestoneIndex]?.tasks || []).filter(
        (_, idx) => idx !== taskIndex
      );
      milestones[milestoneIndex] = {
        ...milestones[milestoneIndex],
        tasks: tasks.length > 0 ? tasks : [createEmptyTask()],
      };
      return { ...prev, milestones };
    });
  };

  const validateForm = () => {
    if (!formValues.name.trim()) {
      toast.error("Template name is required");
      return false;
    }

    for (let i = 0; i < formValues.milestones.length; i++) {
      const milestone = formValues.milestones[i];
      if (!milestone.name.trim()) {
        toast.error(`Milestone ${i + 1} name is required`);
        return false;
      }
      for (let j = 0; j < (milestone.tasks || []).length; j++) {
        const task = milestone.tasks[j];
        if (!task.title.trim()) {
          toast.error(`Task ${j + 1} title is required in milestone ${i + 1}`);
          return false;
        }
      }
    }
    return true;
  };

  const saveTemplate = async () => {
    try {
      if (!validateForm()) return;

      const payload = {
        name: formValues.name.trim(),
        description: formValues.description.trim(),
        status: formValues.status,
        matchCriteria: {
          stage: formValues.criteriaStage ? [formValues.criteriaStage] : [],
          mainGoal: formValues.criteriaMainGoals || [],
          startupType: normalizeStartupTypes(formValues.criteriaStartupTypes),
          ...(formValues.criteriaHasPrototype !== "any"
            ? { hasPrototype: formValues.criteriaHasPrototype === "yes" }
            : {}),
          ...(formValues.criteriaHasPayingUsers !== "any"
            ? { hasPayingUsers: formValues.criteriaHasPayingUsers === "yes" }
            : {}),
        },
        milestones: formValues.milestones.map((milestone, index) => ({
          name: milestone.name.trim(),
          description: milestone.description.trim(),
          order: index,
          targetOffsetDays: Number(milestone.targetOffsetDays || 0),
          keywords: splitCommaValues(milestone.keywordsInput),
          tasks: (milestone.tasks || []).map((task) => ({
            title: task.title.trim(),
            description: task.description.trim(),
            effortEstimate: task.effortEstimate,
            xpValue: Number(task.xpValue || 0),
            priority: task.priority,
            suggestedResourceCodes: [
              ...new Set(
                (Array.isArray(task.suggestedResourceCodes)
                  ? task.suggestedResourceCodes
                  : []
                )
                  .map((code) => `${code || ""}`.trim())
                  .filter(Boolean)
              ),
            ],
          })),
        })),
      };

      if (editing?._id) {
        await axios.put(`/roadmap-template/admin/${editing._id}`, payload, axiosConfig);
        toast.success("Template updated successfully");
      } else {
        await axios.post("/roadmap-template/admin", payload, axiosConfig);
        toast.success("Template created successfully");
      }

      resetForm();
      refreshTable();
    } catch (error) {
      toast.error(error?.response?.data?.message || "Failed to save template");
    }
  };

  const resolveRowId = (rowData) => {
    const row = Array.isArray(rowData) ? rowData[0] : rowData;
    return row?._id || row?.id || null;
  };

  const publishTemplate = async (id) => {
    if (!id || id === "undefined") {
      toast.error("Cannot publish: template id is missing. Refresh and try again.");
      return;
    }
    try {
      await axios.put(`/roadmap-template/admin/${id}/publish`, {}, axiosConfig);
      toast.success("Template published");
      refreshTable();
    } catch (error) {
      toast.error(error?.response?.data?.message || "Failed to publish template");
    }
  };

  const archiveTemplate = async (id) => {
    if (!id || id === "undefined") {
      toast.error("Cannot archive: template id is missing. Refresh and try again.");
      return;
    }
    try {
      await axios.put(`/roadmap-template/admin/${id}/archive`, {}, axiosConfig);
      toast.success("Template archived");
      refreshTable();
    } catch (error) {
      toast.error(error?.response?.data?.message || "Failed to archive template");
    }
  };

  const cloneTemplate = async (id) => {
    if (!id || id === "undefined") {
      toast.error("Cannot clone: template id is missing. Refresh and try again.");
      return;
    }
    try {
      await axios.post(`/roadmap-template/admin/${id}/clone`, {}, axiosConfig);
      toast.success("Template cloned");
      refreshTable();
    } catch (error) {
      toast.error(error?.response?.data?.message || "Failed to clone template");
    }
  };

  const handleBulkUploadSuccess = (result) => {
    const summary = result?.data?.summary;
    if (summary?.templatesCreated || summary?.templatesUpdated) {
      toast.success(
        `Import done: ${summary.templatesCreated || 0} created, ${summary.templatesUpdated || 0} updated`
      );
    } else if (summary?.templatesSkipped) {
      toast.info(`Import finished: ${summary.templatesSkipped} template(s) skipped (already exist)`);
    }
    if (summary?.validationErrors > 0 || summary?.templatesFailed > 0) {
      toast.warn("Some templates had validation errors or failed — review the import dialog");
    } else {
      setBulkUploadOpen(false);
    }
    refreshTable();
  };

  const columns = [
    { title: "Name", field: "name" },
    { title: "Status", field: "status" },
    { title: "Version", field: "version", type: "numeric" },
    {
      title: "Milestones",
      render: (row) =>
        typeof row.milestoneCount === "number"
          ? row.milestoneCount
          : Array.isArray(row.milestones)
          ? row.milestones.length
          : 0,
    },
    {
      title: "Updated",
      render: (row) => (row.updatedAt ? new Date(row.updatedAt).toLocaleString() : ""),
    },
  ];

  return (
    <div className="container" style={{ marginTop: "2rem" }}>
      <HeaderAndLayout activeItem="roadmap-templates" />

      <Grid container justify="flex-end" spacing={2} style={{ marginBottom: "1rem" }}>
        <Grid item>
          <Button
            style={{ backgroundColor: "#1565c0", color: "#fff" }}
            variant="contained"
            startIcon={<CloudDownloadIcon />}
            disabled={exporting || !totalCount}
            onClick={() => setExportDialogOpen(true)}
          >
            {exporting ? "Exporting..." : "Export"}
          </Button>
        </Grid>
        <Grid item>
          <Button
            style={{ backgroundColor: "#009E60", color: "#fff" }}
            variant="contained"
            startIcon={<CloudUploadIcon />}
            onClick={() => setBulkUploadOpen(true)}
          >
            Bulk Upload
          </Button>
        </Grid>
        <Grid item>
          <Button
            style={{ backgroundColor: "#009E60", color: "#fff" }}
            variant="contained"
            onClick={openCreate}
          >
            + Create Template
          </Button>
        </Grid>
      </Grid>

      <Grid container spacing={2} alignItems="center" style={{ marginBottom: "0.75rem" }}>
        <Grid item>
          <Button
            size="small"
            variant="outlined"
            disabled={!totalCount || selectingAll}
            onClick={handleSelectAll}
          >
            {selectingAll
              ? "Selecting..."
              : allSelectedMode
              ? "Unselect"
              : "Select All"}
          </Button>
        </Grid>
        {(selectedRows.length > 0 || allSelectedMode) && (
          <>
            <Grid item>
              <Typography variant="body2" color="textSecondary">
                Selected:{" "}
                {allSelectedMode ? allSelectedIds.length : selectedRows.length}
                {totalCount ? ` / ${totalCount}` : ""}
                {allSelectedMode ? " (all)" : ""}
              </Typography>
            </Grid>
            <Grid item>
              <Button
                size="small"
                variant="contained"
                style={{ backgroundColor: "#d32f2f", color: "#fff" }}
                startIcon={<DeleteIcon />}
                disabled={deleting}
                onClick={handleDeleteSelected}
              >
                Delete Selected
              </Button>
            </Grid>
          </>
        )}
      </Grid>

      {loading && tableData.length === 0 && <BeatLoader />}
      <div
        style={{
          display: loading && tableData.length === 0 ? "none" : "block",
        }}
      >
        <MaterialTable
          tableRef={tableRef}
          title="Roadmap Templates"
          isLoading={loading && tableData.length > 0}
          columns={columns}
          data={(query) =>
            new Promise((resolve, reject) => {
              setLoading(true);
              fetchTablePage(query)
                .then((result) => resolve(result))
                .catch((error) => {
                  toast.error(
                    error?.response?.data?.message || "Failed to load templates"
                  );
                  reject(error);
                })
                .finally(() => setLoading(false));
            })
          }
          options={{
            search: true,
            debounceInterval: 400,
            paging: true,
            pageSize: 10,
            pageSizeOptions: [10, 25, 50],
            paginationType: "stepped",
            actionsColumnIndex: -1,
            selection: true,
            showTextRowsSelected: true,
            rowStyle: { cursor: "pointer" },
          }}
          onSelectionChange={(rows) => {
            const next = rows || [];
            setSelectedRows(next);
            // Manual page selection exits "all rows" mode.
            if (allSelectedModeRef.current) {
              setAllSelectionState(false, []);
            }
          }}
          onRowClick={(event, rowData) =>
            toggleTableRowSelection(event, rowData, tableRef, setSelectedRows)
          }
          actions={[
            {
              icon: "edit",
              tooltip: "Edit Template",
              position: "row",
              hidden: selectedRows.length > 1,
              onClick: (event, rowData) => {
                const row = Array.isArray(rowData) ? rowData[0] : rowData;
                if (row) openEdit(row);
              },
            },
            {
              icon: "delete",
              tooltip: "Delete Template",
              position: "row",
              onClick: (event, rowData) => {
                const id = resolveRowId(rowData);
                if (id) requestDeleteByIds([id], "template");
              },
            },
            {
              icon: "file_copy",
              tooltip: "Clone Template",
              position: "row",
              hidden: selectedRows.length > 1,
              onClick: (event, rowData) => {
                const id = resolveRowId(rowData);
                if (id) cloneTemplate(id);
              },
            },
            (rowData) => {
              const row = Array.isArray(rowData) ? rowData[0] : rowData;
              return {
                icon: "publish",
                tooltip: "Publish Template",
                position: "row",
                hidden: selectedRows.length > 1,
                disabled: !row || row.status === "published",
                onClick: (event, clickedRow) => {
                  const id = resolveRowId(clickedRow) || resolveRowId(row);
                  publishTemplate(id);
                },
              };
            },
            (rowData) => {
              const row = Array.isArray(rowData) ? rowData[0] : rowData;
              return {
                icon: "archive",
                tooltip: "Archive Template",
                position: "row",
                hidden: selectedRows.length > 1,
                disabled: !row || row.status === "archived",
                onClick: (event, clickedRow) => {
                  const id = resolveRowId(clickedRow) || resolveRowId(row);
                  archiveTemplate(id);
                },
              };
            },
          ]}
        />
      </div>

      <Dialog open={open} onClose={resetForm} maxWidth="md" fullWidth>
        <DialogTitle>{editing ? "Edit Roadmap Template" : "Create Roadmap Template"}</DialogTitle>
        <DialogContent>
          <Typography variant="subtitle1" style={{ marginTop: "0.5rem", fontWeight: 600 }}>
            Template Details
          </Typography>
          <TextField
            margin="dense"
            name="name"
            label="Template Name"
            fullWidth
            value={formValues.name}
            onChange={onChange}
          />
          <TextField
            margin="dense"
            name="description"
            label="Description"
            fullWidth
            multiline
            rows={2}
            value={formValues.description}
            onChange={onChange}
          />
          <TextField
            margin="dense"
            name="status"
            label="Status"
            select
            fullWidth
            value={formValues.status}
            onChange={onChange}
          >
            <MenuItem value="draft">Draft</MenuItem>
            <MenuItem value="published">Published</MenuItem>
            <MenuItem value="archived">Archived</MenuItem>
          </TextField>

          <Divider style={{ margin: "1rem 0" }} />
          <Typography variant="subtitle1" style={{ fontWeight: 600 }}>
            Matching Rules
          </Typography>
          <TextField
            margin="dense"
            name="criteriaStage"
            label="Stage"
            select
            fullWidth
            value={formValues.criteriaStage}
            onChange={onChange}
            helperText="Template applies when this onboarding stage matches"
          >
            <MenuItem value="">Any Stage</MenuItem>
            {STAGE_OPTIONS.map((stage) => (
              <MenuItem key={stage} value={stage}>
                {stage}
              </MenuItem>
            ))}
          </TextField>
          <TextField
            margin="dense"
            name="criteriaMainGoals"
            label="Main Goals"
            select
            fullWidth
            SelectProps={{ multiple: true }}
            value={formValues.criteriaMainGoals}
            onChange={onChange}
            helperText="Select one or more goals"
          >
            {MAIN_GOAL_OPTIONS.map((goal) => (
              <MenuItem key={goal} value={goal}>
                {goal}
              </MenuItem>
            ))}
          </TextField>
          <Grid container spacing={1} alignItems="flex-end" style={{ marginTop: "0.25rem" }}>
            <Grid item xs={12} md={9}>
              <TextField
                margin="dense"
                name="criteriaStartupTypeInput"
                label="Startup Type"
                fullWidth
                value={formValues.criteriaStartupTypeInput}
                onChange={onStartupTypeSelectChange}
                select
                helperText="Select to add. If you choose Other, enter a custom type below and press Enter."
              >
                <MenuItem value="">Select...</MenuItem>
                {STARTUP_TYPE_OPTIONS.map((type) => (
                  <MenuItem key={type} value={type}>
                    {type}
                  </MenuItem>
                ))}
              </TextField>
            </Grid>
            {formValues.criteriaStartupTypeInput === "Other" && (
              <Grid item xs={12}>
                <TextField
                  margin="dense"
                  name="criteriaStartupTypeCustomInput"
                  label="Custom Startup Type (optional)"
                  fullWidth
                  value={formValues.criteriaStartupTypeCustomInput}
                  onChange={onChange}
                  onKeyDown={(e) => {
                    if (e.key === "Enter") {
                      e.preventDefault();
                      addOtherStartupType();
                    }
                  }}
                  helperText="Leave empty to match any onboarding custom startup type (wildcard). Press Enter to add."
                />
              </Grid>
            )}
            <Grid item xs={12}>
              <div style={{ display: "flex", flexWrap: "wrap", gap: "0.5rem", marginTop: "0.25rem" }}>
                {(formValues.criteriaStartupTypes || []).map((startupType) => (
                  <Chip
                    key={startupType}
                    label={startupType}
                    onDelete={() => removeStartupType(startupType)}
                    color="primary"
                    variant="outlined"
                    size="small"
                  />
                ))}
              </div>
            </Grid>
          </Grid>
          <TextField
            margin="dense"
            name="criteriaHasPrototype"
            label="Has Prototype"
            select
            fullWidth
            value={formValues.criteriaHasPrototype}
            onChange={onChange}
          >
            <MenuItem value="any">Any</MenuItem>
            <MenuItem value="yes">Yes</MenuItem>
            <MenuItem value="no">No</MenuItem>
          </TextField>
          <TextField
            margin="dense"
            name="criteriaHasPayingUsers"
            label="Has Paying Users"
            select
            fullWidth
            value={formValues.criteriaHasPayingUsers}
            onChange={onChange}
          >
            <MenuItem value="any">Any</MenuItem>
            <MenuItem value="yes">Yes</MenuItem>
            <MenuItem value="no">No</MenuItem>
          </TextField>

          <Divider style={{ margin: "1rem 0" }} />
          <Grid container justify="space-between" alignItems="center">
            <Typography variant="subtitle1" style={{ fontWeight: 600 }}>
              Milestones and Tasks
            </Typography>
            <Button
              variant="outlined"
              color="primary"
              onClick={addMilestone}
              style={{ textTransform: "none" }}
            >
              + Add Milestone
            </Button>
          </Grid>

          {formValues.milestones.map((milestone, milestoneIndex) => (
            <Paper
              key={`milestone-${milestoneIndex}`}
              variant="outlined"
              style={{ padding: "0.75rem", marginTop: "0.75rem" }}
            >
              <Grid container justify="space-between" alignItems="center">
                <Typography variant="subtitle2" style={{ fontWeight: 600 }}>
                  Milestone {milestoneIndex + 1}
                </Typography>
                <IconButton
                  size="small"
                  onClick={() => removeMilestone(milestoneIndex)}
                  title="Remove milestone"
                >
                  x
                </IconButton>
              </Grid>

              <TextField
                margin="dense"
                label="Milestone Name"
                fullWidth
                value={milestone.name}
                onChange={(e) =>
                  updateMilestoneField(milestoneIndex, "name", e.target.value)
                }
              />
              <TextField
                margin="dense"
                label="Milestone Description"
                fullWidth
                multiline
                rows={2}
                value={milestone.description}
                onChange={(e) =>
                  updateMilestoneField(milestoneIndex, "description", e.target.value)
                }
              />
              <Grid container spacing={2}>
                <Grid item xs={12} md={6}>
                  <TextField
                    margin="dense"
                    label="Target Offset (days)"
                    type="number"
                    fullWidth
                    value={milestone.targetOffsetDays}
                    onChange={(e) =>
                      updateMilestoneField(
                        milestoneIndex,
                        "targetOffsetDays",
                        e.target.value
                      )
                    }
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <TextField
                    margin="dense"
                    label="Keywords (comma separated)"
                    fullWidth
                    value={milestone.keywordsInput}
                    onChange={(e) =>
                      updateMilestoneField(milestoneIndex, "keywordsInput", e.target.value)
                    }
                  />
                </Grid>
              </Grid>

              <Divider style={{ margin: "0.75rem 0" }} />
              <Grid container justify="space-between" alignItems="center">
                <Typography variant="subtitle2">Tasks</Typography>
                <Button
                  variant="outlined"
                  size="small"
                  color="primary"
                  onClick={() => addTask(milestoneIndex)}
                  style={{ textTransform: "none" }}
                >
                  + Add Task
                </Button>
              </Grid>

              {(milestone.tasks || []).map((task, taskIndex) => (
                <Paper
                  key={`task-${milestoneIndex}-${taskIndex}`}
                  variant="outlined"
                  style={{ padding: "0.6rem", marginTop: "0.6rem" }}
                >
                  <Grid container justify="space-between" alignItems="center">
                    <Typography variant="body2" style={{ fontWeight: 600 }}>
                      Task {taskIndex + 1}
                    </Typography>
                    <IconButton
                      size="small"
                      onClick={() => removeTask(milestoneIndex, taskIndex)}
                      title="Remove task"
                    >
                      x
                    </IconButton>
                  </Grid>
                  <TextField
                    margin="dense"
                    label="Task Title"
                    fullWidth
                    value={task.title}
                    onChange={(e) =>
                      updateTaskField(milestoneIndex, taskIndex, "title", e.target.value)
                    }
                  />
                  <TextField
                    margin="dense"
                    label="Task Description"
                    fullWidth
                    multiline
                    rows={2}
                    value={task.description}
                    onChange={(e) =>
                      updateTaskField(
                        milestoneIndex,
                        taskIndex,
                        "description",
                        e.target.value
                      )
                    }
                  />
                  <Grid container spacing={2}>
                    <Grid item xs={12} md={4}>
                      <TextField
                        margin="dense"
                        label="Effort Level"
                        select
                        fullWidth
                        value={task.effortEstimate}
                        onChange={(e) =>
                          updateTaskField(
                            milestoneIndex,
                            taskIndex,
                            "effortEstimate",
                            e.target.value
                          )
                        }
                        helperText="How much work this task usually needs"
                      >
                        {TASK_EFFORT_OPTIONS.map((effortLevel) => (
                          <MenuItem key={effortLevel} value={effortLevel}>
                            {effortLevel === "low"
                              ? "Low (quick)"
                              : effortLevel === "high"
                                ? "High (heavy)"
                                : "Medium (standard)"}
                          </MenuItem>
                        ))}
                      </TextField>
                    </Grid>
                    <Grid item xs={12} md={4}>
                      <TextField
                        margin="dense"
                        label="XP Value"
                        type="number"
                        fullWidth
                        value={task.xpValue}
                        onChange={(e) =>
                          updateTaskField(
                            milestoneIndex,
                            taskIndex,
                            "xpValue",
                            e.target.value
                          )
                        }
                      />
                    </Grid>
                    <Grid item xs={12} md={4}>
                      <TextField
                        margin="dense"
                        label="Priority"
                        select
                        fullWidth
                        value={task.priority}
                        onChange={(e) =>
                          updateTaskField(
                            milestoneIndex,
                            taskIndex,
                            "priority",
                            e.target.value
                          )
                        }
                      >
                        {TASK_PRIORITY_OPTIONS.map((priority) => (
                          <MenuItem key={priority} value={priority}>
                            {priority}
                          </MenuItem>
                        ))}
                      </TextField>
                    </Grid>
                  </Grid>
                  <Autocomplete
                    multiple
                    freeSolo
                    options={resourceSearchOptions}
                    value={task.suggestedResourceCodes || []}
                    getOptionLabel={(option) =>
                      typeof option === "string"
                        ? option
                        : `${option.code}${option.name ? ` — ${option.name}` : ""}`
                    }
                    filterSelectedOptions
                    getOptionSelected={(option, value) => {
                      const optionCode =
                        typeof option === "string" ? option : option?.code;
                      const valueCode =
                        typeof value === "string" ? value : value?.code;
                      return optionCode === valueCode;
                    }}
                    onInputChange={(_, value, reason) => {
                      if (reason === "input") {
                        searchResourcesForTask(value);
                      }
                    }}
                    onChange={(_, newValue) => {
                      const codes = [
                        ...new Set(
                          (newValue || [])
                            .map((item) =>
                              typeof item === "string"
                                ? item.trim()
                                : `${item?.code || ""}`.trim()
                            )
                            .filter(Boolean)
                        ),
                      ];
                      updateTaskField(
                        milestoneIndex,
                        taskIndex,
                        "suggestedResourceCodes",
                        codes
                      );
                    }}
                    renderTags={(value, getTagProps) =>
                      value.map((option, index) => (
                        <Chip
                          key={`${option}-${index}`}
                          label={typeof option === "string" ? option : option.code}
                          size="small"
                          {...getTagProps({ index })}
                        />
                      ))
                    }
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        margin="dense"
                        label="Suggested Resources (optional)"
                        placeholder="Search by name or code, then select"
                        helperText="Optional. Resource codes shown as suggested resources for this task in tint-web."
                        InputProps={{
                          ...params.InputProps,
                          endAdornment: (
                            <>
                              {resourceSearchLoading ? (
                                <CircularProgress color="inherit" size={16} />
                              ) : null}
                              {params.InputProps.endAdornment}
                            </>
                          ),
                        }}
                      />
                    )}
                  />
                </Paper>
              ))}
            </Paper>
          ))}
        </DialogContent>
        <DialogActions>
          <Button onClick={resetForm} color="secondary" variant="outlined">
            Cancel
          </Button>
          <Button onClick={saveTemplate} variant="contained" style={{ backgroundColor: "#0066B2", color: "#fff" }}>
            Save
          </Button>
        </DialogActions>
      </Dialog>

      <BulkUploadDialog
        open={bulkUploadOpen}
        onClose={() => setBulkUploadOpen(false)}
        onUploadSuccess={handleBulkUploadSuccess}
        axiosConfig={axiosConfig}
      />

      <ExportFormatDialog
        open={exportDialogOpen}
        onClose={() => {
          if (!exporting) setExportDialogOpen(false);
        }}
        onExport={handleExport}
        exporting={exporting}
        title="Export roadmap templates"
        description="Choose CSV or Excel for your roadmap templates export."
      />

      <ConfirmDialog
        open={confirmDialog.open}
        title={confirmDialog.title}
        message={confirmDialog.message}
        confirmLabel="Delete"
        cancelLabel="Cancel"
        confirmColor="danger"
        loading={deleting}
        onCancel={() => {
          if (!deleting) {
            setConfirmDialog({ open: false, title: "", message: "", ids: [] });
          }
        }}
        onConfirm={handleConfirmDelete}
      />

      <ToastContainer position="bottom-right" autoClose={5000} />
    </div>
  );
}

export default RoadmapTemplates;
